<template>
  <div class="edit-teams-user">
    <div class="popup">
      <div class="head">
        <span class="name">{{ $t('team_popup.edit_user_title', { name: user.name }) }}</span>
        <UiIcon
          :size="20"
          class="close"
          name="times"
          @click="close"
        />
      </div>
      <UiScrollbar
        wrap-style="max-height: 60vh; height: auto;"
        :native="false"
      >
        <div
          v-loading="isLoading"
          class="body"
        >
          <UiInput
            v-model="userChange.name"
            class="t:mb-16"
            :error="errors.name"
            :placeholder="$t(`team_popup.name_placeholder`)"
            :label="$t(`team_popup.name_label`)"
            size="big"
            autosize
          />
          <UiInput
            v-model="userChange.email"
            class="t:mb-16"
            :error="errors.email"
            :placeholder="$t(`team_popup.email_placeholder`)"
            :label="$t(`team_popup.email_label`)"
            size="big"
            autosize
          />
          <UiSelectElement
            v-model="userChange.timezone"
            class="t:mb-16"
            :placeholder="$t(`team_popup.timezone_placeholder`)"
            :label="$t(`team_popup.timezone_label`)"
            :list="timezones"
            option-value-param="zone"
            filterable
            size="big"
          />
          <div class="select-wrap">
            <div class="select-block select-block__full">
              <div class="t:flex t:items-start">
                <UiLabel
                  class="t:mb-8"
                  :label="$t(`team_popup.see_stats_label`)"
                />
                <UiCheckbox
                  v-model="isOverall"
                  :label="$t(`team_popup.see_stats_overall`)"
                  class="t:ml-8"
                />
              </div>
              <div class="date-block">
                <UiDatePicker
                  v-model="userChange.entities_visibility_from"
                  class="t:w-full"
                  :disabled="isOverall"
                  :placeholder="$t(`team_popup.see_stats_placeholder`)"
                  :multi-calendars="false"
                  :range="false"
                  teleport
                  auto-apply
                  :always-clearable="false"
                  :is-error="!!getError('entities_visibility_from')"
                />
                <div class="spacer">
                  –
                </div>
                <UiDatePicker
                  v-model="userChange.entities_visibility_to"
                  class="t:w-full"
                  :disabled="isOverall"
                  :placeholder="$t(`team_popup.see_stats_placeholder`)"
                  :multi-calendars="false"
                  :range="false"
                  teleport
                  auto-apply
                  :always-clearable="false"
                  :is-error="!!getError('entities_visibility_to')"
                />
              </div>
              <UiHelp
                v-if="$_.isArray(errors.entities_visibility_to)"
                :text="$t(`${errors.entities_visibility_to[0].message}`)"
                is-error
              />
            </div>
          </div>

          <div class="select-wrap">
            <div class="select-block">
              <UiSelectElement
                v-model="userChange.messenger"
                :placeholder="$t(`team_popup.messenger_placeholder`)"
                :label="$t(`team_popup.messenger_label`)"
                :list="messengers"
                size="big"
              />
            </div>
            <div class="select-block">
              <UiInput
                v-model="userChange.messenger_id"
                :error="errors.messenger_id"
                :disabled="!userChange.messenger"
                :placeholder="messengerPlaceholder"
                :label="$t(`team_popup.messenger_id_label`)"
                size="big"
                autosize
              />
            </div>
          </div>
          <UiTextarea
            v-model="userChange.notes"
            :label="$t('team_popup.note_label')"
            :placeholder="$t(`team_popup.note_placeholder`)"
            :rows="3"
            :error="errors.notes"
          />
        </div>
      </UiScrollbar>
      <div class="foot t:space-x-8">
        <UiButton
          type="negative"
          icon="times"
          @click="close"
        >
          {{ $t(`team_popup.cancel_button`) }}
        </UiButton>
        <UiButton
          filled
          icon="check"
          @click="save"
        >
          {{ $t(`team_popup.save_button`) }}
        </UiButton>
      </div>
    </div>
    <div class="wrapper" />
  </div>
</template>

<script>

import { endOfDay, format, startOfDay } from 'date-fns';

export default {
  name: 'EditTeamsUser',
  props: {
    user: {
      type: Object,
      default() {
        return {};
      },
    },
    timezones: {
      type: Array,
      default() {
        return [];
      },
    },
    messengers: {
      type: Array,
      default() {
        return [];
      },
    },
    isOpen: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['close', 'refresh'],

  data() {
    return {
      errors: {},
      userChange: {},
      isOverall: false,
      isLoading: false,
    };
  },

  computed: {
    messengerPlaceholder: {
      get() {
        if (this.userChange.messenger) {
          const placeholder = this.messengers ? this.$_.find(this.messengers, ['id', this.userChange.messenger]) : '';
          return placeholder ? placeholder.example_placeholder : '';
        }
        return `${this.$t('team_popup.messenger_id_placeholder')}`;
      },
    },
  },

  watch: {
    isOpen() {
      this.userChange = this.$_.pick(this.user, [
        'name',
        'email',
        'timezone',
        'messenger',
        'messenger_id',
        'notes',
        'entities_visibility_from',
        'entities_visibility_to',
      ]);
      this.isOverall = this.userChange.entities_visibility_from === null 
        && this.userChange.entities_visibility_to === null;
    },
  },

  mounted() {

  },

  methods: {
    getError(key) {
      return this.$_.get(this.errors, [key], '');
    },
    close() {
      this.errors = {};
      this.$emit('close');
    },

    save() {
      this.isLoading = true;
      this.errors = {};
      if (this.isOverall) {
        this.userChange.entities_visibility_from = null;
        this.userChange.entities_visibility_to = null;
      } else {
        this.userChange.entities_visibility_from = this.userChange.entities_visibility_from === null ? null : format(startOfDay(this.userChange.entities_visibility_from), 'yyyy-MM-dd HH:mm:ss');
        this.userChange.entities_visibility_to = this.userChange.entities_visibility_to === null ? null : format(endOfDay(this.userChange.entities_visibility_to), 'yyyy-MM-dd HH:mm:ss');
      }
      this.$api
        .putAdmins(this.user.id, this.userChange)
        .then((response) => {
          this.$emit('refresh', response);
          this.$emit('close');
        })
        .catch((error) => {
          this.errors = this.$_.cloneDeepWith(error.data.errors);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

  },
};
</script>
